<script setup>
import { ref, onMounted, computed } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();
const isRtl = computed(() => locale.value === "ar");

const defaultImage = "/path/to/default-image.png"; // ضع مسار الصورة الافتراضية هنا

const getBannerImage = (scholarship) => {
  if (
    scholarship.banners &&
    scholarship.banners.length &&
    scholarship.banners[0].image_url
  ) {
    return scholarship.banners[0].image_url;
  }
  return defaultImage;
};
</script>
<template>
  <div class="d-flex flex-column flex-column-fluid">
    <!-- Toolbar -->
    <div id="kt_app_toolbar" class="app-toolbar py-3 py-lg-6">
      <div
        id="kt_app_toolbar_container"
        class="app-container container-fluid d-flex flex-stack"
      >
        <div
          class="page-title d-flex flex-column justify-content-center flex-wrap me-3"
        >
          <h1 class="page-heading text-dark fw-bold fs-3 my-0">
            {{ $t("sch.details.title") }}
          </h1>
          <ul
            class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1"
          >
            <li class="breadcrumb-item text-muted">
              <router-link to="/" class="text-muted text-hover-primary">{{
                $t("common.home")
              }}</router-link>
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">
              {{ scholarship?.name_ar || scholarship?.name_original }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- المحتوى -->
    <div id="kt_app_content" class="app-content flex-column-fluid">
      <div id="kt_app_content_container" class="app-container container-fluid">
        <!-- التحميل -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-success" role="status"></div>
          <p class="mt-3 fw-bold text-secondary">
            {{ $t("sch.details.loading") }}
          </p>
        </div>

        <!-- التفاصيل -->
        <div v-else-if="scholarship" class="card shadow-lg p-4 mt-4">
          <!-- زر المفضلة الرئيسي -->
          <!-- زر المفضلة -->
          <div class="mb-3 d-flex justify-content-end">
            <button
              class="btn btn-pill shadow-sm d-flex align-items-center gap-1"
              :class="isFavorite ? 'btn-danger' : 'btn-secondary'"
              @click="toggleFavorite"
              :disabled="favoriteLoading"
            >
              <i class="fa fa-heart text-white"></i>
              <span v-if="favoriteLoading" class="d-flex align-items-center">
                <div
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></div>
                {{ $t("sch.details.processing") }}
              </span>
              <span v-else>
                {{
                  isFavorite
                    ? $t("sch.details.remove_fav")
                    : $t("sch.details.add_fav")
                }}
              </span>
            </button>
          </div>

          <Swiper
            v-if="scholarship.banners && scholarship.banners.length"
            :modules="[Pagination, Autoplay]"
            :slides-per-view="1"
            :loop="true"
            :autoplay="{ delay: 3000, disableOnInteraction: false }"
            :pagination="{ clickable: true }"
            :dir="isRtl ? 'rtl' : 'ltr'"
            class="banner-swiper"
          >
            <SwiperSlide v-for="banner in scholarship.banners" :key="banner.id">
              <div class="banner-slide">
                <img
                  :src="banner.image_url || defaultImage"
                  class="banner-img"
                  @error="handleImageError"
                />
              </div>
            </SwiperSlide>
          </Swiper>

          <h2 class="fw-bold text-success mb-3">
            {{ scholarship.name_ar || scholarship.name_original }}
          </h2>

          <!-- المؤسسة المضيفة -->
          <div v-if="scholarship.host_institution" class="mb-3">
            <h5 class="fw-bold text-dark">
              {{ $t("sch.details.host_institution") }}:
            </h5>
            <p class="mb-0">
              <strong>{{
                scholarship.host_institution.name_ar ||
                scholarship.host_institution.name
              }}</strong>
              <span v-if="scholarship.host_institution.website">
                -
                <a
                  :href="scholarship.host_institution.website"
                  target="_blank"
                  class="text-primary"
                  >{{ $t("sch.details.visit_website") }}</a
                >
              </span>
            </p>
          </div>

          <!-- معلومات أساسية -->
          <div class="row gy-3">
            <div
              v-for="(value, key) in displayFields"
              :key="key"
              class="col-md-6"
            >
              <div class="border rounded p-3 bg-light">
                <strong>{{ $t(`sch.details.labels.${key}`) }}:</strong>
                <p class="mb-0">{{ value }}</p>
              </div>
            </div>
          </div>

          <!-- الفوائد -->
          <div v-if="scholarship.benefits?.length" class="mt-5">
            <h5 class="fw-bold text-dark mb-3">
              {{ $t("sch.details.benefits_title") }}
            </h5>
            <table class="table table-bordered text-center align-middle">
              <thead class="table-success">
                <tr>
                  <th>{{ $t("sch.details.benefits.type") }}</th>
                  <th>{{ $t("sch.details.benefits.amount") }}</th>
                  <th>{{ $t("sch.details.benefits.currency") }}</th>
                  <th>{{ $t("sch.details.benefits.frequency") }}</th>
                  <th>{{ $t("sch.details.benefits.description") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in scholarship.benefits" :key="b.id">
                  <td>{{ b.type }}</td>
                  <td>{{ b.amount }}</td>
                  <td>{{ b.currency }}</td>
                  <td>{{ b.frequency }}</td>
                  <td>{{ b.description }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- المتطلبات -->
          <div v-if="scholarship.requirements?.length" class="mt-5">
            <h5 class="fw-bold text-dark mb-3">
              {{ $t("sch.details.requirements_title") }}
            </h5>
            <ul class="list-group">
              <li
                v-for="req in scholarship.requirements"
                :key="req.id"
                class="list-group-item"
              >
                <i class="fa fa-check text-success me-2"></i>
                {{ req.description }}
              </li>
            </ul>
          </div>

          <!-- الدول المؤهلة -->
          <div v-if="scholarship.eligible_countries?.length" class="mt-5">
            <h5 class="fw-bold text-dark mb-3">
              {{ $t("sch.details.eligible_countries") }}
            </h5>
            <div class="d-flex flex-wrap gap-2">
              <span
                style="background-color: #00c444 !important"
                v-for="c in scholarship.eligible_countries"
                :key="c.id"
                class="badge bg-success px-3 py-2 fs-7"
              >
                {{ c.nicename }}
              </span>
            </div>
          </div>

          <!-- رابط التقديم -->
          <div v-if="scholarship.official_link" class="mt-5 text-center">
            <a
              :href="scholarship.official_link"
              target="_blank"
              class="btn btn-success btn-lg px-5"
            >
              {{ $t("sch.details.apply_official") }}
            </a>
          </div>
        </div>

        <div v-else class="alert alert-warning mt-5 text-center">
          {{ $t("sch.details.not_found") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const defaultImage = "/path/to/default-image.png"; // ضع مسار الصورة الافتراضية هنا

import { apiUrl } from "@/utils/api";
export default {
  name: "ScholarshipsDetails",
  data() {
    return {
      isFavorite: false, // حالة المفضلة للمنحة
      defaultImage: "/path/to/default-image.png", // ضع مسار الصورة الافتراضية هنا
      displayedScholarships: [],
      isLoading: true,
      scholarship: null,
      scholarId: this.$route.query.id,
      token: localStorage.getItem("access_token"),
      labels: {
        type: "نوع المنحة",
        level: "المستوى الدراسي",
        city: "المدينة",
        funding_type: "نوع التمويل",
        tuition_fees: "الرسوم الدراسية",
        program_language: "لغة البرنامج",
        study_mode: "نظام الدراسة",
        duration_months: "المدة (بالأشهر)",
        seats_available: "عدد المقاعد المتاحة",
        start_date: "تاريخ البدء",
        application_open: "فتح باب التقديم",
        application_deadline: "آخر موعد للتقديم",
        academic_status: "المستوى الأكاديمي المطلوب",
        required_gpa: "الحد الأدنى للمعدل",
        language_requirement: "متطلبات اللغة",
        min_age: "الحد الأدنى للعمر",
        max_age: "الحد الأقصى للعمر",
      },
    };
  },
  computed: {
    displayFields() {
      if (!this.scholarship) return {};
      const fields = {};
      for (const [key, label] of Object.entries(this.labels)) {
        const value = this.scholarship[key];
        if (value && value !== "null" && value !== null) {
          fields[key] = value;
        }
      }
      return fields;
    },
  },
  async mounted() {
    await this.fetchScholarshipDetails();
  },
  methods: {
    async fetchScholarshipDetails() {
      try {
        const response = await fetch(
          `${apiUrl}/scholarships/${this.scholarId}`,
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        const res = await response.json();
        this.scholarship = res.data;
        this.displayedScholarships = res.data.banners;
        // بعد this.scholarship = res.data;
        if (this.token) {
          const favRes = await fetch(
            `${apiUrl}/scholarships/${this.scholarId}/favorite/check`,
            {
              headers: {
                Authorization: `Bearer ${this.token}`,
              },
            }
          );
          const favData = await favRes.json();
          this.isFavorite = favData.data.is_favorite;
        }
      } catch (error) {
        console.error("Error fetching scholarship:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async toggleFavorite() {
      if (!this.token) {
        Swal.fire({
          icon: "warning",
          title: "تنبيه",
          text: "يجب تسجيل الدخول أولاً",
        });
        return;
      }

      const url = `${apiUrl}/scholarships/${this.scholarId}/favorite`;
      const method = this.isFavorite ? "DELETE" : "POST";

      try {
        const response = await fetch(url, {
          method,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        const res = await response.json();
        this.isFavorite = !this.isFavorite;

        /*Swal.fire({
          icon: this.isFavorite ? "success" : "info",
          title: this.isFavorite ? "تمت الإضافة للمفضلة" : "تمت الإزالة من المفضلة",
          showConfirmButton: false,
          timer: 1500,
          position: "top-end",
          toast: true,
        });*/
      } catch (error) {
        console.error("Error toggling favorite:", error);
        Swal.fire({
          icon: "error",
          title: "حدث خطأ",
          text: "تعذر تحديث المفضلة، حاول لاحقاً",
        });
      }
    },
  },
};
</script>

<style scoped>
/* ========== Main Card ========== */
.card {
  border-radius: 24px;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.08);
  position: relative;
  overflow: hidden;
}
.card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #00c444, #00e050, #00c444);
  background-size: 200% 100%;
  animation: gradientSlide 3s linear infinite;
}
@keyframes gradientSlide {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}
.card.shadow-lg {
  box-shadow: 0 20px 60px rgba(0, 196, 68, 0.1), 0 8px 25px rgba(0, 0, 0, 0.06) !important;
}

/* ========== Table Styles ========== */
.table {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.08);
}
.table th {
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%) !important;
  color: #0f5132;
  font-weight: 700;
  border: none !important;
  padding: 14px 16px;
}
.table td {
  padding: 12px 16px;
  vertical-align: middle;
  border-color: rgba(0, 196, 68, 0.1) !important;
}
.table tbody tr {
  transition: all 0.3s ease;
}
.table tbody tr:hover {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fff5 100%);
  transform: scale(1.01);
}
.table-success {
  --bs-table-bg: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%);
  --bs-table-color: #1b1b1b;
}
.table-bordered > :not(caption) > * > * {
  border-color: rgba(0, 196, 68, 0.15);
}

/* ========== Badge Styles ========== */
.badge {
  font-size: 0.85rem;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.2);
}
.badge:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
}
.badge.bg-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
}

/* ========== Banner Swiper ========== */
.banner-swiper {
  width: 100%;
  max-width: 100%;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(0, 196, 68, 0.12);
}
.banner-slide {
  width: 100%;
  height: 380px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}
.banner-slide::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, transparent 70%, rgba(0, 0, 0, 0.3) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 1;
}
.banner-slide:hover::before {
  opacity: 1;
}
.banner-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 20px;
  background-color: #fff;
  transition: transform 0.5s ease;
}
.banner-slide:hover .banner-img {
  transform: scale(1.03);
}

/* ========== Swiper Pagination ========== */
:deep(.swiper-pagination-bullet) {
  background: rgba(0, 196, 68, 0.3);
  opacity: 1;
  width: 10px;
  height: 10px;
  transition: all 0.3s ease;
}
:deep(.swiper-pagination-bullet-active) {
  background: linear-gradient(135deg, #00c444 0%, #00e050 100%);
  width: 28px;
  border-radius: 6px;
}

/* ========== Buttons ========== */
.btn-pill {
  border-radius: 999px;
  font-weight: 600;
  padding: 10px 24px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
}
.btn-pill::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}
.btn-pill:hover::before {
  left: 100%;
}
.btn-pill:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.btn-danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  border: none;
  color: #fff;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}
.btn-danger:hover {
  background: linear-gradient(135deg, #e04555 0%, #d32f3f 100%);
  box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  border: none;
  color: #fff;
  box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
}
.btn-secondary:hover {
  background: linear-gradient(135deg, #7c858d 0%, #6a7278 100%);
  box-shadow: 0 8px 25px rgba(108, 117, 125, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  border: none !important;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.35);
}
.btn-success:hover {
  background: linear-gradient(135deg, #00d44a 0%, #00b844 100%) !important;
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0, 196, 68, 0.45);
}

/* ========== Text Colors ========== */
.text-success {
  color: #00c444 !important;
}
h2.text-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.75rem;
}

/* ========== Info Boxes ========== */
.border.rounded.p-3.bg-light {
  border: none !important;
  border-radius: 16px !important;
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%) !important;
  border-left: 4px solid #00c444 !important;
  padding: 16px 20px !important;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.06);
}
.border.rounded.p-3.bg-light:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.12);
  border-left-width: 6px !important;
}
.border.rounded.p-3.bg-light strong {
  color: #00a43c;
  font-size: 0.9rem;
}
.border.rounded.p-3.bg-light p {
  color: #1a1a2e;
  font-weight: 500;
  margin-top: 6px;
}

/* ========== Requirements List ========== */
.list-group {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.08);
}
.list-group-item {
  border: none;
  border-bottom: 1px solid rgba(0, 196, 68, 0.1);
  padding: 14px 20px;
  transition: all 0.3s ease;
  background: #fff;
}
.list-group-item:last-child {
  border-bottom: none;
}
.list-group-item:hover {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fff5 100%);
  padding-right: 28px;
}
.list-group-item i {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%);
  border-radius: 50%;
  font-size: 10px;
}

/* ========== Section Headings ========== */
h5.fw-bold.text-dark {
  position: relative;
  padding-right: 16px;
  color: #1a1a2e !important;
}
h5.fw-bold.text-dark::before {
  content: "";
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #00c444 0%, #00e050 100%);
  border-radius: 4px;
}

/* ========== Links ========== */
a {
  color: #00a43c;
  transition: all 0.3s ease;
  text-decoration: none;
}
a:hover {
  color: #00c444;
  text-decoration: underline;
}

/* ========== Form Controls ========== */
.form-control:focus,
.form-select:focus {
  border-color: #00c444 !important;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.12) !important;
}

/* ========== Loading Spinner ========== */
.spinner-border.text-success {
  color: #00c444 !important;
  width: 3rem;
  height: 3rem;
}
.text-center.py-5 {
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 20px;
  padding: 60px !important;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.08);
}

/* ========== Host Institution ========== */
.mb-3:has(h5:contains("المؤسسة")) {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fff5 100%);
  padding: 16px 20px;
  border-radius: 16px;
  border-left: 4px solid #00c444;
}

/* ========== Eligible Countries Section ========== */
.d-flex.flex-wrap.gap-2 {
  padding: 16px;
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 16px;
  border: 1px solid rgba(0, 196, 68, 0.1);
}

/* ========== Apply Button Section ========== */
.mt-5.text-center:has(.btn-success.btn-lg) {
  padding: 30px;
  background: linear-gradient(135deg, #f0fff5 0%, #e8fff1 100%);
  border-radius: 20px;
  border: 2px dashed rgba(0, 196, 68, 0.3);
}

/* ========== Row Animation ========== */
.row.gy-3 > div {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}
.row.gy-3 > div:nth-child(1) {
  animation-delay: 0.05s;
}
.row.gy-3 > div:nth-child(2) {
  animation-delay: 0.1s;
}
.row.gy-3 > div:nth-child(3) {
  animation-delay: 0.15s;
}
.row.gy-3 > div:nth-child(4) {
  animation-delay: 0.2s;
}
.row.gy-3 > div:nth-child(5) {
  animation-delay: 0.25s;
}
.row.gy-3 > div:nth-child(6) {
  animation-delay: 0.3s;
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== Alert ========== */
.alert-warning {
  border-radius: 20px;
  border: none;
  background: linear-gradient(135deg, #fff8e6 0%, #fff3d4 100%);
  box-shadow: 0 8px 30px rgba(255, 193, 7, 0.15);
  padding: 30px;
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .card {
    border-radius: 18px;
    padding: 16px !important;
  }
  .banner-slide {
    height: 250px;
  }
  .banner-swiper {
    border-radius: 16px;
  }
  h2.text-success {
    font-size: 1.4rem;
  }
  .border.rounded.p-3.bg-light {
    padding: 12px 16px !important;
  }
}

/* ========== Scrollbar ========== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: #f0fff5;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #00c444 0%, #00a43c 100%);
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #00d44a 0%, #00b844 100%);
}

/* ========== DARK MODE ========== */
[data-bs-theme="dark"] .card,
[data-theme="dark"] .card,
.dark .card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .card.shadow-lg,
[data-theme="dark"] .card.shadow-lg,
.dark .card.shadow-lg {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), 0 8px 25px rgba(0, 196, 68, 0.1) !important;
}

/* Banner Dark Mode */
[data-bs-theme="dark"] .banner-slide,
[data-theme="dark"] .banner-slide,
.dark .banner-slide {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%);
}
[data-bs-theme="dark"] .banner-swiper,
[data-theme="dark"] .banner-swiper,
.dark .banner-swiper {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

/* Title Dark Mode */
[data-bs-theme="dark"] h2.text-success,
[data-theme="dark"] h2.text-success,
.dark h2.text-success {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Info Boxes Dark Mode */
[data-bs-theme="dark"] .border.rounded.p-3.bg-light,
[data-theme="dark"] .border.rounded.p-3.bg-light,
.dark .border.rounded.p-3.bg-light {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%) !important;
  border-left-color: #00c444 !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .border.rounded.p-3.bg-light:hover,
[data-theme="dark"] .border.rounded.p-3.bg-light:hover,
.dark .border.rounded.p-3.bg-light:hover {
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .border.rounded.p-3.bg-light strong,
[data-theme="dark"] .border.rounded.p-3.bg-light strong,
.dark .border.rounded.p-3.bg-light strong {
  color: #00e050;
}
[data-bs-theme="dark"] .border.rounded.p-3.bg-light p,
[data-theme="dark"] .border.rounded.p-3.bg-light p,
.dark .border.rounded.p-3.bg-light p {
  color: #e4e6eb;
}

/* Table Dark Mode */
[data-bs-theme="dark"] .table,
[data-theme="dark"] .table,
.dark .table {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .table th,
[data-theme="dark"] .table th,
.dark .table th {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%) !important;
  color: #8bffb3;
}
[data-bs-theme="dark"] .table td,
[data-theme="dark"] .table td,
.dark .table td {
  background: #242526;
  color: #e4e6eb;
  border-color: rgba(0, 196, 68, 0.15) !important;
}
[data-bs-theme="dark"] .table tbody tr:hover,
[data-theme="dark"] .table tbody tr:hover,
.dark .table tbody tr:hover {
  background: linear-gradient(135deg, #2d2e30 0%, #1a3d2a 100%);
}

/* Badge Dark Mode */
[data-bs-theme="dark"] .badge,
[data-theme="dark"] .badge,
.dark .badge {
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.3);
}
[data-bs-theme="dark"] .badge.bg-success,
[data-theme="dark"] .badge.bg-success,
.dark .badge.bg-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
}

/* List Group Dark Mode */
[data-bs-theme="dark"] .list-group,
[data-theme="dark"] .list-group,
.dark .list-group {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .list-group-item,
[data-theme="dark"] .list-group-item,
.dark .list-group-item {
  background: #242526;
  color: #e4e6eb;
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .list-group-item:hover,
[data-theme="dark"] .list-group-item:hover,
.dark .list-group-item:hover {
  background: linear-gradient(135deg, #2d2e30 0%, #1a3d2a 100%);
}
[data-bs-theme="dark"] .list-group-item i,
[data-theme="dark"] .list-group-item i,
.dark .list-group-item i {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  color: #00e050;
}

/* Section Headings Dark Mode */
[data-bs-theme="dark"] h5.fw-bold.text-dark,
[data-theme="dark"] h5.fw-bold.text-dark,
.dark h5.fw-bold.text-dark {
  color: #e4e6eb !important;
}
[data-bs-theme="dark"] h5.fw-bold.text-dark::before,
[data-theme="dark"] h5.fw-bold.text-dark::before,
.dark h5.fw-bold.text-dark::before {
  background: linear-gradient(180deg, #00e050 0%, #00c444 100%);
}

/* Host Institution Dark Mode */
[data-bs-theme="dark"] .mb-3 p,
[data-theme="dark"] .mb-3 p,
.dark .mb-3 p {
  color: #b0b3b8;
}
[data-bs-theme="dark"] .mb-3 strong,
[data-theme="dark"] .mb-3 strong,
.dark .mb-3 strong {
  color: #e4e6eb;
}

/* Eligible Countries Section Dark Mode */
[data-bs-theme="dark"] .d-flex.flex-wrap.gap-2,
[data-theme="dark"] .d-flex.flex-wrap.gap-2,
.dark .d-flex.flex-wrap.gap-2 {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%);
  border-color: rgba(0, 196, 68, 0.2);
}

/* Buttons Dark Mode */
[data-bs-theme="dark"] .btn-pill.btn-danger,
[data-theme="dark"] .btn-pill.btn-danger,
.dark .btn-pill.btn-danger {
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
}
[data-bs-theme="dark"] .btn-pill.btn-secondary,
[data-theme="dark"] .btn-pill.btn-secondary,
.dark .btn-pill.btn-secondary {
  background: linear-gradient(135deg, #4a4b4d 0%, #3a3b3d 100%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .btn-success,
[data-theme="dark"] .btn-success,
.dark .btn-success {
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.4);
}

/* Links Dark Mode */
[data-bs-theme="dark"] a,
[data-theme="dark"] a,
.dark a {
  color: #00e050;
}
[data-bs-theme="dark"] a:hover,
[data-theme="dark"] a:hover,
.dark a:hover {
  color: #8bffb3;
}

/* Loading Spinner Dark Mode */
[data-bs-theme="dark"] .text-center.py-5,
[data-theme="dark"] .text-center.py-5,
.dark .text-center.py-5 {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .text-center.py-5 p,
[data-theme="dark"] .text-center.py-5 p,
.dark .text-center.py-5 p {
  color: #b0b3b8;
}

/* Alert Dark Mode */
[data-bs-theme="dark"] .alert-warning,
[data-theme="dark"] .alert-warning,
.dark .alert-warning {
  background: linear-gradient(135deg, #3d3520 0%, #4a3f1a 100%);
  color: #ffd666;
  box-shadow: 0 8px 30px rgba(255, 193, 7, 0.1);
}

/* Breadcrumb Dark Mode */
[data-bs-theme="dark"] .breadcrumb-item.text-muted,
[data-theme="dark"] .breadcrumb-item.text-muted,
.dark .breadcrumb-item.text-muted {
  color: #b0b3b8 !important;
}
[data-bs-theme="dark"] .breadcrumb-item a,
[data-theme="dark"] .breadcrumb-item a,
.dark .breadcrumb-item a {
  color: #00e050 !important;
}

/* Page Heading Dark Mode */
[data-bs-theme="dark"] .page-heading,
[data-theme="dark"] .page-heading,
.dark .page-heading {
  color: #e4e6eb;
}

/* Swiper Pagination Dark Mode */
[data-bs-theme="dark"] :deep(.swiper-pagination-bullet),
[data-theme="dark"] :deep(.swiper-pagination-bullet),
.dark :deep(.swiper-pagination-bullet) {
  background: rgba(0, 196, 68, 0.4);
}
[data-bs-theme="dark"] :deep(.swiper-pagination-bullet-active),
[data-theme="dark"] :deep(.swiper-pagination-bullet-active),
.dark :deep(.swiper-pagination-bullet-active) {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
}

/* Scrollbar Dark Mode */
[data-bs-theme="dark"] ::-webkit-scrollbar-track,
[data-theme="dark"] ::-webkit-scrollbar-track,
.dark ::-webkit-scrollbar-track {
  background: #242526;
}
</style>
